import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { OnboardingAPI } from '../api';
import { PageShell, ErrorBanner, P } from '../components/onboarding/Shell';
import Step1 from '../components/onboarding/Step1';
import Step2 from '../components/onboarding/Step2';
import Step3 from '../components/onboarding/Step3';
import { PUBLIC_SITE_NAME, SIGNED_IN_PRODUCT_NAME } from '../config/brand';

const PAGE_FIELDS = { 1: ['terms_intro'], 2: ['name', 'phone', 'email', 'terms_data', 'signature'] };

// The agreement date is always the day it is signed; the server stamps the authoritative value.
const todayParts = () => {
  const d = new Date();
  return { month: String(d.getMonth() + 1).padStart(2, '0'), day: String(d.getDate()).padStart(2, '0'), year: String(d.getFullYear()) };
};

const scrollToError = (errors) => {
  const first = Object.keys(errors)[0];
  const el = first && document.getElementById(`ob-${first}`);
  if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.querySelector('input,select,canvas')?.focus?.({ preventScroll: true }); }
};

export default function Landing() {
  const { user, login } = useAuth();
  const [page, setPage] = useState(1);
  const [data, setData] = useState(() => ({ date: todayParts() }));
  const [errors, setErrors] = useState({});
  const [attempted, setAttempted] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [captcha, setCaptcha] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [pdf, setPdf] = useState(null);
  const draftIdRef = useRef(null);
  const draftSavedRef = useRef('');

  // Remember a started-but-unsigned agreement so the team can follow up.
  useEffect(() => {
    if (page !== 2 || done) return;
    const email = (data.email || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
    const key = `${email}|${data.first_name || ''}|${data.last_name || ''}|${data.phone || ''}`;
    if (key === draftSavedRef.current) return;
    const t = setTimeout(() => {
      OnboardingAPI.saveDraft({ draft_id: draftIdRef.current, email, first_name: data.first_name, last_name: data.last_name, phone: data.phone })
        .then((res) => { draftIdRef.current = res.draft_id || draftIdRef.current; draftSavedRef.current = key; })
        .catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
  }, [page, done, data.email, data.first_name, data.last_name, data.phone]);

  const refreshChallenge = useCallback(() => {
    setCaptcha('');
    OnboardingAPI.challenge()
      .then((c) => setChallenge((prev) => ({ ...c, reset: (prev?.reset || 0) + 1 })))
      .catch(() => setChallenge({ mode: 'arithmetic', question: 'Challenge unavailable — please retry' }));
  }, []);
  useEffect(() => { refreshChallenge(); }, [refreshChallenge]);

  const validatePage = useCallback(async (p, d) => {
    const res = await OnboardingAPI.validate(d, PAGE_FIELDS[p]);
    const errs = { ...res.errors };
    if (p === 2 && !captcha.trim()) errs.captcha = 'Recaptcha requires verification.';
    return errs;
  }, [captcha]);

  const setField = (k, v) => {
    const next = { ...data, [k]: v };
    setData(next);
    if (attempted) validatePage(page, next).then(setErrors).catch(() => {});
  };

  const next = async () => {
    setAttempted(true);
    const errs = await validatePage(page, data);
    setErrors(errs);
    if (Object.keys(errs).length) { scrollToError(errs); return; }
    setAttempted(false);
    setPage(page + 1);
    window.scrollTo({ top: 0 });
  };

  const back = () => { setErrors({}); setAttempted(false); setPage(page - 1); window.scrollTo({ top: 0 }); };

  const submit = async () => {
    setSubmitting(true);
    try {
      const verification = challenge?.mode === 'recaptcha' ? { recaptcha_token: captcha } : { challenge_id: challenge?.id, challenge_answer: captcha };
      const res = await OnboardingAPI.submit(data, verification);
      setEmailStatus(res.copy_email_status);
      setPdf(res.pdf_url ? { url: res.pdf_url, filename: res.pdf_filename } : null);
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 422 && detail?.errors) {
        setErrors(detail.errors);
        setAttempted(true);
        if (detail.errors.captcha) refreshChallenge();
        setPage(detail.errors.terms_intro && Object.keys(detail.errors).length === 1 ? 1 : 2);
        setTimeout(() => scrollToError(detail.errors), 50);
      } else {
        setErrors({ submit: 'Submission failed. Please try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const errorCount = Object.keys(errors).filter((k) => k !== 'submit').length;

  return (
    <PageShell>
      <ErrorBanner count={errorCount} onSee={() => scrollToError(errors)} />
      {done ? (
        <div className="border-t border-white/70 pt-10 pb-20 text-center" data-testid="onboarding-success">
          <h2 className="text-[30px] md:text-[40px] font-extrabold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }} data-testid="thank-you-heading">
            Thank You {[data.first_name, data.last_name].filter(Boolean).join(' ')}!
          </h2>
          <P className="text-[17px] md:text-[19px] text-[#2EE6C3] mt-2">Your submission has been received.</P>

          {pdf?.url && (
            <div className="mt-8 mx-auto max-w-[900px] bg-white/95 rounded-md shadow-lg px-4 md:px-5 py-4 flex flex-col sm:flex-row items-center gap-3" data-testid="agreement-download-card">
              <span className="w-9 h-9 rounded bg-[#E8EEFF] flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-[#3E64FF]" />
              </span>
              <span className="text-[15px] text-[#2C3345] truncate flex-1 text-left w-full" data-testid="agreement-pdf-filename">{pdf.filename}</span>
              <a href={`${pdf.url}&download=1`} target="_blank" rel="noreferrer" data-testid="thankyou-download-pdf"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-[15px] font-semibold text-[#2C3345] hover:bg-[#F0F1FB] w-full sm:w-auto">
                <Download className="w-4 h-4" /> Download PDF
              </a>
            </div>
          )}

          <P className="text-[14px] text-white/80 mt-6" data-testid={`agreement-copy-${emailStatus}`}>
            {emailStatus === 'sent' && `Your signed agreement has been emailed to ${data.email} as a PDF attachment.`}
            {emailStatus === 'failed' && 'We could not email your copy right now — please download it above to keep a copy.'}
            {emailStatus === 'not_configured' && 'Email delivery is not configured yet; please download your copy above.'}
          </P>
          <P className="text-[15px] mt-2">Continue to {SIGNED_IN_PRODUCT_NAME} to sign up and access your {PUBLIC_SITE_NAME} workspace.</P>
          <div className="mt-6 flex justify-center gap-3">
            {user ? (
              <Link to="/workspace" data-testid="landing-open-app" className="px-8 py-2.5 text-[17px] text-white rounded-[4px]" style={{ backgroundColor: 'rgba(0,102,83,0.85)' }}>Open {SIGNED_IN_PRODUCT_NAME}</Link>
            ) : (
              <button onClick={login} data-testid="landing-signup" className="px-8 py-2.5 text-[17px] text-white rounded-[4px]" style={{ backgroundColor: 'rgba(0,102,83,0.85)' }}>Sign up to {SIGNED_IN_PRODUCT_NAME} with Google</button>
            )}
          </div>
        </div>
      ) : page === 1 ? (
        <Step1 data={data} setField={setField} errors={errors} onNext={next} />
      ) : page === 2 ? (
        <Step2 data={data} setField={setField} errors={errors} onNext={next} onBack={back} challenge={challenge} captcha={captcha} setCaptcha={(v) => { setCaptcha(v); if (attempted) setErrors((e) => { const c = { ...e }; if (v.trim()) delete c.captcha; return c; }); }} refreshChallenge={refreshChallenge} />
      ) : (
        <Step3 data={data} onBack={back} onSubmit={submit} submitting={submitting} errors={errors} />
      )}
      <div className="h-16" />
    </PageShell>
  );
}
